import { startInternalMonitoring, combine, canUseEventBridge, startBatchWithReplica, getEventBridge } from '@datadog/browser-core';
import { createDOMMutationObservable } from '../browser/domMutationObservable';
import { startPerformanceCollection } from '../browser/performanceCollection';
import { startRumAssembly } from '../domain/assembly';
import { startForegroundContexts } from '../domain/foregroundContexts';
import { startInternalContext } from '../domain/internalContext';
import { LifeCycle } from '../domain/lifeCycle';
import { startViewContexts } from '../domain/viewContexts';
import { startRequestCollection } from '../domain/requestCollection';
import { startActionCollection } from '../domain/rumEventsCollection/action/actionCollection';
import { startErrorCollection } from '../domain/rumEventsCollection/error/errorCollection';
import { startLongTaskCollection } from '../domain/rumEventsCollection/longTask/longTaskCollection';
import { startResourceCollection } from '../domain/rumEventsCollection/resource/resourceCollection';
import { startViewCollection } from '../domain/rumEventsCollection/view/viewCollection';
import { startRumSessionManager, startRumSessionManagerStub } from '../domain/rumSessionManager';
import { startRumBatch } from '../transport/startRumBatch';
import { startRumEventBridge } from '../transport/startRumEventBridge';
import { startUrlContexts } from '../domain/urlContexts';
import { createLocationChangeObservable } from '../browser/locationChangeObservable';
export function startRum(configuration, getCommonContext, recorderApi, initialViewOptions) {
  var lifeCycle = new LifeCycle();
  var internalMonitoring = startRumInternalMonitoring(configuration);
  internalMonitoring.setExternalContextProvider(function () {
    var _a;

    return combine({
      application_id: configuration.applicationId,
      session: {
        id: (_a = session.findTrackedSession()) === null || _a === void 0 ? void 0 : _a.id
      }
    }, viewContexts.findView(), {
      view: {
        name: null
      }
    });
  });
  internalMonitoring.setTelemetryContextProvider(function () {
    var _a, _b;

    return {
      application: {
        id: configuration.applicationId
      },
      session: {
        id: (_a = session.findTrackedSession()) === null || _a === void 0 ? void 0 : _a.id
      },
      view: {
        id: (_b = viewContexts.findView()) === null || _b === void 0 ? void 0 : _b.view.id
      },
      action: {
        id: actionContexts.findActionId()
      }
    };
  });

  if (!canUseEventBridge()) {
    startRumBatch(configuration, lifeCycle, internalMonitoring.telemetryEventObservable);
  } else {
    startRumEventBridge(lifeCycle);
  }

  var session = !canUseEventBridge() ? startRumSessionManager(configuration, lifeCycle) : startRumSessionManagerStub();
  var domMutationObservable = createDOMMutationObservable();
  var locationChangeObservable = createLocationChangeObservable(location);

  var _a = startRumEventCollection(lifeCycle, configuration, location, session, locationChangeObservable, domMutationObservable, getCommonContext),
      viewContexts = _a.viewContexts,
      foregroundContexts = _a.foregroundContexts,
      urlContexts = _a.urlContexts,
      actionContexts = _a.actionContexts,
      addAction = _a.addAction;

  startLongTaskCollection(lifeCycle, session);
  startResourceCollection(lifeCycle);

  var _b = startViewCollection(lifeCycle, configuration, location, domMutationObservable, locationChangeObservable, foregroundContexts, recorderApi, initialViewOptions),
      addTiming = _b.addTiming,
      startView = _b.startView;

  var addError = startErrorCollection(lifeCycle, foregroundContexts).addError;
  startRequestCollection(lifeCycle, configuration, session);
  startPerformanceCollection(lifeCycle, configuration);
  var internalContext = startInternalContext(configuration.applicationId, session, viewContexts, actionContexts, urlContexts);
  return {
    addAction: addAction,
    addError: addError,
    addTiming: addTiming,
    startView: startView,
    lifeCycle: lifeCycle,
    viewContexts: viewContexts,
    session: session,
    getInternalContext: internalContext.get
  };
}

function startRumInternalMonitoring(configuration) {
  var _a;

  var internalMonitoring = startInternalMonitoring(configuration);

  if (canUseEventBridge()) {
    var bridge_1 = getEventBridge();
    internalMonitoring.monitoringMessageObservable.subscribe(function (message) {
      return bridge_1.send('internal_log', message);
    });
    internalMonitoring.telemetryEventObservable.subscribe(function (message) {
      return bridge_1.send('internal_telemetry', message);
    });
  } else if (configuration.internalMonitoringEndpointBuilder) {
    var batch_1 = startBatchWithReplica(configuration, configuration.internalMonitoringEndpointBuilder, (_a = configuration.replica) === null || _a === void 0 ? void 0 : _a.internalMonitoringEndpointBuilder);
    internalMonitoring.monitoringMessageObservable.subscribe(function (message) {
      return batch_1.add(message);
    });
  }

  return internalMonitoring;
}

export function startRumEventCollection(lifeCycle, configuration, location, sessionManager, locationChangeObservable, domMutationObservable, getCommonContext) {
  var viewContexts = startViewContexts(lifeCycle);
  var urlContexts = startUrlContexts(lifeCycle, locationChangeObservable, location);
  var foregroundContexts = startForegroundContexts();

  var _a = startActionCollection(lifeCycle, domMutationObservable, configuration, foregroundContexts),
      addAction = _a.addAction,
      actionContexts = _a.actionContexts;

  startRumAssembly(configuration, lifeCycle, sessionManager, viewContexts, urlContexts, actionContexts, getCommonContext);
  return {
    viewContexts: viewContexts,
    foregroundContexts: foregroundContexts,
    urlContexts: urlContexts,
    addAction: addAction,
    actionContexts: actionContexts,
    stop: function stop() {
      viewContexts.stop();
      foregroundContexts.stop();
    }
  };
}